// src/components/cuenta/AuthSection.js
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import './AuthSection.css';
import loginVideo from '../../assets/login-video.mp4';

// --- ICONOS SVG ----
const MailVerifyIcon = () => (
  <svg viewBox="0 0 24 24" width="50" height="50" fill="#0052FF">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

// Componente `KeyIcon` que renderiza esta parte de la interfaz.
const KeyIcon = () => (
  <svg viewBox="0 0 24 24" width="50" height="50" fill="#0052FF">
    <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
  </svg>
);

// Componente `EyeOpenIcon` que renderiza esta parte de la interfaz.
const EyeOpenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

// Componente `EyeClosedIcon` que renderiza esta parte de la interfaz.
const EyeClosedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
    <line x1="2" x2="22" y1="2" y2="22"></line>
  </svg>
);

// Componente `CheckIcon` que renderiza esta parte de la interfaz.
const CheckIcon = ({ active }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke={active ? "#0052FF" : "#ccc"} strokeWidth="4"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: 'stroke 0.3s ease' }}>
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

// Componente `CircularTimer` que renderiza esta parte de la interfaz.
const CircularTimer = ({ seconds }) => {
  // Valor intermedio `radius` utilizado por la lógica de este bloque.
  const radius = 20;
  // Valor intermedio `circumference` utilizado por la lógica de este bloque.
  const circumference = 2 * Math.PI * radius;
  // Valor intermedio `progress` utilizado por la lógica de este bloque.
  const progress = (seconds / 60) * circumference;
  // Valor intermedio `angle` utilizado por la lógica de este bloque.
  const angle = (seconds / 60) * 360 - 90;
  // Valor intermedio `ballX` utilizado por la lógica de este bloque.
  const ballX = 25 + radius * Math.cos((angle * Math.PI) / 180);
  // Valor intermedio `ballY` utilizado por la lógica de este bloque.
  const ballY = 25 + radius * Math.sin((angle * Math.PI) / 180);
  return (
    <div className="circular-timer-wrapper">
      <svg width="45" height="45" viewBox="0 0 50 50">
        <circle className="timer-track" cx="25" cy="25" r={radius} />
        <circle className="timer-progress" cx="25" cy="25" r={radius}
          strokeDasharray={circumference} strokeDashoffset={circumference - progress} />
        <circle className="timer-ball" cx={ballX} cy={ballY} r="3" />
      </svg>
      <span className="timer-number">{seconds}</span>
    </div>
  );
};

// ============================================
// Reglas de teléfono por país
// ============================================
const COUNTRY_PHONE_RULES = [
  { code: '1809', country: 'Rep. Dominicana', iso: 'DO', digits: [10] },
  { code: '1829', country: 'Rep. Dominicana', iso: 'DO', digits: [10] },
  { code: '1849', country: 'Rep. Dominicana', iso: 'DO', digits: [10] },
  { code: '1', country: 'EE.UU./Canadá', iso: 'US', digits: [10] },
  { code: '52', country: 'México', iso: 'MX', digits: [10] },
  { code: '54', country: 'Argentina', iso: 'AR', digits: [10, 11] },
  { code: '55', country: 'Brasil', iso: 'BR', digits: [10, 11] },
  { code: '56', country: 'Chile', iso: 'CL', digits: [9] },
  { code: '57', country: 'Colombia', iso: 'CO', digits: [10] },
  { code: '58', country: 'Venezuela', iso: 'VE', digits: [10] },
  { code: '51', country: 'Perú', iso: 'PE', digits: [9] },
  { code: '53', country: 'Cuba', iso: 'CU', digits: [8] },
  { code: '591', country: 'Bolivia', iso: 'BO', digits: [8] },
  { code: '593', country: 'Ecuador', iso: 'EC', digits: [9] },
  { code: '595', country: 'Paraguay', iso: 'PY', digits: [9] },
  { code: '598', country: 'Uruguay', iso: 'UY', digits: [8, 9] },
  { code: '502', country: 'Guatemala', iso: 'GT', digits: [8] },
  { code: '503', country: 'El Salvador', iso: 'SV', digits: [8] },
  { code: '504', country: 'Honduras', iso: 'HN', digits: [8] },
  { code: '505', country: 'Nicaragua', iso: 'NI', digits: [8] },
  { code: '506', country: 'Costa Rica', iso: 'CR', digits: [8] },
  { code: '507', country: 'Panamá', iso: 'PA', digits: [7, 8] },
  { code: '509', country: 'Haití', iso: 'HT', digits: [8] },
  { code: '34', country: 'España', iso: 'ES', digits: [9] },
  { code: '33', country: 'Francia', iso: 'FR', digits: [9] },
  { code: '44', country: 'Reino Unido', iso: 'GB', digits: [10] },
  { code: '49', country: 'Alemania', iso: 'DE', digits: [10, 11] },
  { code: '39', country: 'Italia', iso: 'IT', digits: [9, 10] },
  { code: '351', country: 'Portugal', iso: 'PT', digits: [9] },
  { code: '31', country: 'Países Bajos', iso: 'NL', digits: [9] },
  { code: '32', country: 'Bélgica', iso: 'BE', digits: [9] },
  { code: '41', country: 'Suiza', iso: 'CH', digits: [9] },
  { code: '43', country: 'Austria', iso: 'AT', digits: [10] },
  { code: '46', country: 'Suecia', iso: 'SE', digits: [9] },
  { code: '47', country: 'Noruega', iso: 'NO', digits: [8] },
  { code: '45', country: 'Dinamarca', iso: 'DK', digits: [8] },
  { code: '358', country: 'Finlandia', iso: 'FI', digits: [9, 10] },
  { code: '48', country: 'Polonia', iso: 'PL', digits: [9] },
  { code: '420', country: 'Rep. Checa', iso: 'CZ', digits: [9] },
  { code: '36', country: 'Hungría', iso: 'HU', digits: [9] },
  { code: '40', country: 'Rumania', iso: 'RO', digits: [9] },
  { code: '30', country: 'Grecia', iso: 'GR', digits: [10] },
  { code: '353', country: 'Irlanda', iso: 'IE', digits: [9] },
  { code: '380', country: 'Ucrania', iso: 'UA', digits: [9] },
  { code: '7', country: 'Rusia', iso: 'RU', digits: [10] },
  { code: '81', country: 'Japón', iso: 'JP', digits: [10] },
  { code: '82', country: 'Corea del Sur', iso: 'KR', digits: [10, 11] },
  { code: '86', country: 'China', iso: 'CN', digits: [11] },
  { code: '91', country: 'India', iso: 'IN', digits: [10] },
  { code: '65', country: 'Singapur', iso: 'SG', digits: [8] },
  { code: '66', country: 'Tailandia', iso: 'TH', digits: [9] },
  { code: '63', country: 'Filipinas', iso: 'PH', digits: [10] },
  { code: '60', country: 'Malasia', iso: 'MY', digits: [9, 10] },
  { code: '62', country: 'Indonesia', iso: 'ID', digits: [10, 11, 12] },
  { code: '84', country: 'Vietnam', iso: 'VN', digits: [9, 10] },
  { code: '90', country: 'Turquía', iso: 'TR', digits: [10] },
  { code: '61', country: 'Australia', iso: 'AU', digits: [9] },
  { code: '64', country: 'Nueva Zelanda', iso: 'NZ', digits: [8, 9] },
  { code: '972', country: 'Israel', iso: 'IL', digits: [9] },
  { code: '971', country: 'EAU', iso: 'AE', digits: [9] },
  { code: '966', country: 'Arabia Saudita', iso: 'SA', digits: [9] },
  { code: '27', country: 'Sudáfrica', iso: 'ZA', digits: [9] },
  { code: '234', country: 'Nigeria', iso: 'NG', digits: [10] },
  { code: '254', country: 'Kenia', iso: 'KE', digits: [9] },
  { code: '20', country: 'Egipto', iso: 'EG', digits: [10] },
  { code: '212', country: 'Marruecos', iso: 'MA', digits: [9] },
];

// Constante `SORTED_PHONE_RULES` que centraliza un valor de configuración compartido.
const SORTED_PHONE_RULES = [...COUNTRY_PHONE_RULES].sort(
  (a, b) => b.code.length - a.code.length
);

// Constante `DISPOSABLE_EMAIL_DOMAINS` que centraliza un valor de configuración compartido.
const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com','throwaway.email','guerrillamail.com','mailinator.com',
  'yopmail.com','tempail.com','fakeinbox.com','sharklasers.com',
  'guerrillamailblock.com','grr.la','dispostable.com','trashmail.com',
  'trashmail.net','trashmail.me','temp-mail.org','temp-mail.io',
  'getnada.com','emailondeck.com','mohmal.com','burnermail.io',
  'maildrop.cc','10minutemail.com','minutemail.com','tempinbox.com',
  'harakirimail.com','mailnesia.com','mailcatch.com','trash-mail.com',
];

// Constante `EMAIL_TYPO_CORRECTIONS` que centraliza un valor de configuración compartido.
const EMAIL_TYPO_CORRECTIONS = {
  'gmial.com':'gmail.com','gmal.com':'gmail.com','gamil.com':'gmail.com',
  'gnail.com':'gmail.com','gmaill.com':'gmail.com','gmail.con':'gmail.com',
  'gmail.co':'gmail.com','gmail.cm':'gmail.com','gmai.com':'gmail.com',
  'gmali.com':'gmail.com','hotmal.com':'hotmail.com','hotmial.com':'hotmail.com',
  'hotmai.com':'hotmail.com','hotmail.con':'hotmail.com','hotmaill.com':'hotmail.com',
  'hotamil.com':'hotmail.com','outlok.com':'outlook.com','outllok.com':'outlook.com',
  'ourlook.com':'outlook.com','outlook.con':'outlook.com','outook.com':'outlook.com',
  'yaho.com':'yahoo.com','yahooo.com':'yahoo.com','yhaoo.com':'yahoo.com',
  'yahoo.con':'yahoo.com','yahho.com':'yahoo.com','iclud.com':'icloud.com',
  'icould.com':'icloud.com','icloud.con':'icloud.com','protonmal.com':'protonmail.com',
  'protonmail.con':'protonmail.com','live.con':'live.com',
};

// Constante `VALID_TLDS` que centraliza un valor de configuración compartido.
const VALID_TLDS = [
  'com','net','org','edu','gov','mil','int','info','biz','name','pro','coop',
  'museum','aero','jobs','travel','tel','mobi',
  'mx','us','uk','ca','br','ar','cl','co','pe','ec','ve','bo','py','uy','cr',
  'pa','gt','sv','hn','ni','cu','do','ht','pr','es','fr','de','it','pt','nl',
  'be','ch','at','se','no','dk','fi','pl','cz','hu','ro','gr','ie','ua','ru',
  'jp','kr','cn','in','au','nz','sg','th','ph','my','id','vn','tr','il','ae',
  'sa','za','ng','ke','eg','ma','io','ai','app','dev','tech','online','store',
  'shop','site','web','cloud','digital',
  'com.mx','com.br','com.ar','com.co','co.uk','com.au','co.jp','co.kr',
  'com.cn','co.in','com.sg','com.tr','co.za','com.eg','gob.mx','edu.mx','org.mx',
];

// Función `detectCountry` que encapsula una operación reutilizable de este módulo.
function detectCountry(phoneDigits) {
  for (const rule of SORTED_PHONE_RULES) {
    if (phoneDigits.startsWith(rule.code)) return rule;
  }
  return null;
}

// Función `getPhoneGroups` que obtiene la información necesaria para el flujo.
function getPhoneGroups(country, nationalLength) {
  if (country?.iso === 'MX') return [2, 4, 4];
  if (country?.iso === 'US' || country?.iso === 'CA') return [3, 3, 4];
  if (country?.iso === 'AR' || country?.iso === 'BR') {
    return nationalLength > 10 ? [2, 5, 4] : [2, 4, 4];
  }

  if (nationalLength <= 7) return [3, 4];
  if (nationalLength === 8) return [4, 4];
  if (nationalLength === 9) return [3, 3, 3];
  if (nationalLength === 10) return [3, 3, 4];
  if (nationalLength === 11) return [3, 4, 4];
  return [4, 4, 4];
}

// Función `groupPhoneDigits` que encapsula una operación reutilizable de este módulo.
function groupPhoneDigits(digits, groups) {
  // Colección `parts` que reúne los elementos utilizados en este flujo.
  const parts = [];
  // Valor intermedio `offset` utilizado por la lógica de este bloque.
  let offset = 0;

  for (const size of groups) {
    if (offset >= digits.length) break;
    parts.push(digits.slice(offset, offset + size));
    offset += size;
  }

  if (offset < digits.length) parts.push(digits.slice(offset));
  return parts.join('-');
}

// Función `formatPhoneNumber` que transforma un valor al formato requerido.
function formatPhoneNumber(phoneValue) {
  // Valor intermedio `digits` utilizado por la lógica de este bloque.
  const digits = String(phoneValue || '').replace(/\D/g, '');
  if (!digits) return '+';

  // Valor intermedio `country` utilizado por la lógica de este bloque.
  const country = detectCountry(digits);
  if (!country) return `+${digits}`;

  // Valor intermedio `nationalNumber` utilizado por la lógica de este bloque.
  const nationalNumber = digits.slice(country.code.length);
  if (!nationalNumber) return `(+${country.code})`;

  // Valor intermedio `targetLength` utilizado por la lógica de este bloque.
  const targetLength = Math.max(...country.digits);
  // Valor intermedio `groups` utilizado por la lógica de este bloque.
  const groups = getPhoneGroups(country, targetLength);
  return `(+${country.code}) ${groupPhoneDigits(nationalNumber, groups)}`;
}

// Función `normalizePhoneNumber` que transforma un valor al formato requerido.
function normalizePhoneNumber(phoneValue) {
  // Valor intermedio `digits` utilizado por la lógica de este bloque.
  const digits = String(phoneValue || '').replace(/\D/g, '').slice(0, 15);
  return digits ? `+${digits}` : '';
}

// Función `validatePhone` que evalúa la condición indicada por su nombre.
function validatePhone(phoneValue) {
  if (!phoneValue || phoneValue === '+') return { valid: false, error: '', country: null };
  // Valor intermedio `digits` utilizado por la lógica de este bloque.
  const digits = phoneValue.replace(/\D/g, '');
  if (digits.length < 1) return { valid: false, error: '', country: null };
  // Valor intermedio `country` utilizado por la lógica de este bloque.
  const country = detectCountry(digits);
  if (country) {
    // Valor intermedio `nat` utilizado por la lógica de este bloque.
    const nat = digits.slice(country.code.length);
    // Valor intermedio `minD` utilizado por la lógica de este bloque.
    const minD = Math.min(...country.digits);
    // Valor intermedio `maxD` utilizado por la lógica de este bloque.
    const maxD = Math.max(...country.digits);
    if (nat.length < minD) return { valid: false, error: `Faltan dígitos (${country.digits.join(' o ')} después de +${country.code})`, country };
    if (nat.length > maxD) return { valid: false, error: `Demasiados dígitos (máx ${maxD})`, country };
    return { valid: true, error: '', country, info: '✓ Número válido' };
  }
  if (digits.length < 7) return { valid: false, error: 'Incluye código de país (ej: +52)', country: null };
  if (digits.length > 15) return { valid: false, error: 'Máximo 15 dígitos', country: null };
  return { valid: true, error: '', country: null, info: '✓ Formato válido' };
}

// Función `validateEmail` que evalúa la condición indicada por su nombre.
function validateEmail(emailValue) {
  if (!emailValue) return { valid: false, error: '', suggestion: null };
  if (/\s/.test(emailValue)) return { valid: false, error: 'Sin espacios', suggestion: null };
  // Valor intermedio `atCount` utilizado por la lógica de este bloque.
  const atCount = (emailValue.match(/@/g) || []).length;
  if (atCount === 0) return { valid: false, error: 'Falta @', suggestion: null };
  if (atCount > 1) return { valid: false, error: 'Solo un @', suggestion: null };
  // Valores extraídos de una estructura para usarlos directamente en este bloque.
  const [localPart, domainPart] = emailValue.split('@');
  if (!localPart) return { valid: false, error: 'Falta usuario', suggestion: null };
  if (localPart.length > 64) return { valid: false, error: 'Usuario muy largo', suggestion: null };
  if (!domainPart) return { valid: false, error: 'Falta dominio', suggestion: null };
  if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(localPart)) return { valid: false, error: 'Caracteres inválidos', suggestion: null };
  if (!domainPart.includes('.')) return { valid: false, error: 'Falta extensión (.com)', suggestion: null };
  // Valor intermedio `lowerDomain` utilizado por la lógica de este bloque.
  const lowerDomain = domainPart.toLowerCase();
  if (DISPOSABLE_EMAIL_DOMAINS.includes(lowerDomain)) return { valid: false, error: 'Correo temporal no permitido', suggestion: null };
  // Valor intermedio `typo` utilizado por la lógica de este bloque.
  const typo = EMAIL_TYPO_CORRECTIONS[lowerDomain];
  // Valor intermedio `suggestion` utilizado por la lógica de este bloque.
  let suggestion = typo ? `${localPart}@${typo}` : null;
  // Valor intermedio `dp` utilizado por la lógica de este bloque.
  const dp = lowerDomain.split('.');
  // Valor intermedio `lastTwo` utilizado por la lógica de este bloque.
  const lastTwo = dp.slice(-2).join('.');
  // Valor intermedio `lastOne` utilizado por la lógica de este bloque.
  const lastOne = dp[dp.length - 1];
  if (!VALID_TLDS.includes(lastTwo) && !VALID_TLDS.includes(lastOne)) return { valid: false, error: `".${lastOne}" no reconocida`, suggestion };
  // Valor intermedio `re` utilizado por la lógica de este bloque.
  const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  if (!re.test(emailValue)) return { valid: false, error: 'Formato inválido', suggestion };
  return { valid: true, error: '', suggestion };
}

// Función `validateName` que evalúa la condición indicada por su nombre.
function validateName(value) {
  if (!value) return { valid: false, error: '' };
  if (value.trim().length < 2) return { valid: false, error: 'Mínimo 2 caracteres' };
  if (value.trim().length > 50) return { valid: false, error: 'Máximo 50 caracteres' };
  return { valid: true, error: '' };
}

// --- COMPONENTE PRINCIPAL ---
export default function AuthSection({ onLogin, onNavigate, t = {} }) {
  // Estado local `view` y su actualizador, usados para reflejar cambios en la interfaz.
  const [view, setView] = useState(() => {
    // Valor intermedio `params` utilizado por la lógica de este bloque.
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'resetPassword' && params.get('token')) {
      return 'resetPassword';
    }
    if (params.get('view') === 'invite' && params.get('token')) {
      return 'invite';
    }
    return 'login';
  });

  useEffect(() => {
    if (view === 'resetPassword' || view === 'invite') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [view]);

  // El encabezado puede solicitar login aunque AuthSection ya este montado.
  useEffect(() => {
    const openLogin = () => setView('login');
    window.addEventListener('wisby:open-login', openLogin);
    return () => window.removeEventListener('wisby:open-login', openLogin);
  }, []);

  // Estado local `showSuccessModal` y su actualizador, usados para reflejar cambios en la interfaz.
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // La variable debe apuntar al origen del backend. Normalizamos también valores
  // configurados como "https://backend.example/api" para evitar /api/api/...
  // entre los entornos de Vercel (producción, vista previa y desarrollo).
  const API_URL = (process.env.REACT_APP_API_URL || '')
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/api$/i, '');

  // Función `logAuthApiError` que encapsula una operación reutilizable de este módulo.
  const logAuthApiError = (endpoint, response, data = {}) => {
    // No registrar datos personales ni credenciales; estos campos bastan para
    // localizar la petición exacta en los logs de Railway.
    console.error('[Auth API] Solicitud rechazada', {
      method: 'POST',
      url: `${API_URL}${endpoint}`,
      status: response.status,
      requestId: data.requestId || response.headers.get('x-request-id') || 'N/A',
      message: data.msg || response.statusText || 'Error desconocido',
    });
  };

  // OTP
  const [otp, setOtp] = useState(new Array(6).fill(""));
  // Estado local `activeUserEmail` y su actualizador, usados para reflejar cambios en la interfaz.
  const [activeUserEmail, setActiveUserEmail] = useState('');
  // Estado local `resendTimer` y su actualizador, usados para reflejar cambios en la interfaz.
  const [resendTimer, setResendTimer] = useState(0);
  // Estado local `verifyError` y su actualizador, usados para reflejar cambios en la interfaz.
  const [verifyError, setVerifyError] = useState('');
  // Referencia mutable `otpInputs` que conserva datos entre renderizados sin provocar uno nuevo.
  const otpInputs = useRef([]);

  // Login
  const [loginEmail, setLoginEmail] = useState('');
  // Estado local `loginPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [loginPassword, setLoginPassword] = useState('');
  // Estado local `showLoginPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  // Estado local `loginMessage` y su actualizador, usados para reflejar cambios en la interfaz.
  const [loginMessage, setLoginMessage] = useState('');
  // Estado local `rememberMe` y su actualizador, usados para reflejar cambios en la interfaz.
  const [rememberMe, setRememberMe] = useState(true);
  // 🔴 FIX: Estado para prevenir múltiples envíos de login
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);

  // Registro
  const [name, setName] = useState('');
  // Estado local `lastName` y su actualizador, usados para reflejar cambios en la interfaz.
  const [lastName, setLastName] = useState('');
  // Estado local `email` y su actualizador, usados para reflejar cambios en la interfaz.
  const [email, setEmail] = useState('');
  // Estado local `password` y su actualizador, usados para reflejar cambios en la interfaz.
  const [password, setPassword] = useState('');
  // Estado local `confirmPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [confirmPassword, setConfirmPassword] = useState('');
  // Estado local `phone` y su actualizador, usados para reflejar cambios en la interfaz.
  const [phone, setPhone] = useState('+');
  // Estado local `acceptPolicies` y su actualizador, usados para reflejar cambios en la interfaz.
  const [acceptPolicies, setAcceptPolicies] = useState(false);
  // Estado local `showPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [showPassword, setShowPassword] = useState(false);
  // Estado local `showConfirmPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Estado local `registerError` y su actualizador, usados para reflejar cambios en la interfaz.
  const [registerError, setRegisterError] = useState('');
  // 🔴 FIX: Estado para prevenir múltiples envíos de registro
  const [isRegisterSubmitting, setIsRegisterSubmitting] = useState(false);
  const [registerPhoneStep, setRegisterPhoneStep] = useState(false);

  // Forgot Password
  const [forgotEmail, setForgotEmail] = useState('');
  // Estado local `forgotMessage` y su actualizador, usados para reflejar cambios en la interfaz.
  const [forgotMessage, setForgotMessage] = useState('');
  // Estado local `forgotSuccess` y su actualizador, usados para reflejar cambios en la interfaz.
  const [forgotSuccess, setForgotSuccess] = useState(false);
  // Estado local `forgotLoading` y su actualizador, usados para reflejar cambios en la interfaz.
  const [forgotLoading, setForgotLoading] = useState(false);

  // Reset Password
  const [resetNewPassword, setResetNewPassword] = useState('');
  // Estado local `resetConfirmPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  // Estado local `showResetPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [showResetPassword, setShowResetPassword] = useState(false);
  // Estado local `showResetConfirmPassword` y su actualizador, usados para reflejar cambios en la interfaz.
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  // Estado local `resetMessage` y su actualizador, usados para reflejar cambios en la interfaz.
  const [resetMessage, setResetMessage] = useState('');
  // Estado local `resetSuccess` y su actualizador, usados para reflejar cambios en la interfaz.
  const [resetSuccess, setResetSuccess] = useState(false);
  // Estado local `resetLoading` y su actualizador, usados para reflejar cambios en la interfaz.
  const [resetLoading, setResetLoading] = useState(false);
  // Estado local `resetToken` y su actualizador, usados para reflejar cambios en la interfaz.
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get('token') || '');

  // Rol y notario comparten el formulario de invitaciones.
  const [inviteKind] = useState(() => new URLSearchParams(window.location.search).get('kind') === 'role' ? 'role' : 'notario');
  const isInvitation = view === 'invite';
  const [inviteRoleNames, setInviteRoleNames] = useState([]);

  // Invitación de Notario (Parte 5)
  const [inviteToken] = useState(() => {
    // Valor intermedio `params` utilizado por la lógica de este bloque.
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'invite' ? params.get('token') || '' : '';
  });
  // Estado local `inviteNotarioName` y su actualizador, usados para reflejar cambios en la interfaz.
  const [inviteNotarioName, setInviteNotarioName] = useState('');
  // Estado local `inviteMessage` y su actualizador, usados para reflejar cambios en la interfaz.
  const [inviteMessage, setInviteMessage] = useState('');
  // Estado local `inviteSuccess` y su actualizador, usados para reflejar cambios en la interfaz.
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteExistingAccount, setInviteExistingAccount] = useState(false);
  // Estado local `inviteVerified` y su actualizador, usados para reflejar cambios en la interfaz.
  const [inviteVerified, setInviteVerified] = useState(false);
  // Estado local `inviteVerifying` y su actualizador, usados para reflejar cambios en la interfaz.
  const [inviteVerifying, setInviteVerifying] = useState(true);

  // Validación
  const [touched, setTouched] = useState({});
  // Estado local `emailSuggestion` y su actualizador, usados para reflejar cambios en la interfaz.
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  useEffect(() => {
    // Valor intermedio `interval` utilizado por la lógica de este bloque.
    let interval;
    if (resendTimer > 0) interval = setInterval(() => setResendTimer(p => p - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Valor `passwordCriteria` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const passwordCriteria = useMemo(() => ({
    length: password.length >= 8,
    number: /\d/.test(password),
    case: /[a-z]/.test(password) && /[A-Z]/.test(password),
    special: /[*_#$/@!%&]/.test(password),
  }), [password]);

  // Valor `resetPasswordCriteria` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const resetPasswordCriteria = useMemo(() => ({
    length: resetNewPassword.length >= 8,
    number: /\d/.test(resetNewPassword),
    case: /[a-z]/.test(resetNewPassword) && /[A-Z]/.test(resetNewPassword),
    special: /[*_#$/@!%&]/.test(resetNewPassword),
  }), [resetNewPassword]);

  // Valor `isResetFormComplete` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const isResetFormComplete = useMemo(() => (
    resetNewPassword.trim() !== '' &&
    resetConfirmPassword === resetNewPassword &&
    Object.values(resetPasswordCriteria).every(Boolean)
  ), [resetNewPassword, resetConfirmPassword, resetPasswordCriteria]);

  // Verificación del token de invitación al montar la vista (Parte 5)
  useEffect(() => {
    if (view !== 'invite' || !inviteToken) {
      setInviteVerifying(false);
      return;
    }
    // Valor intermedio `cancelled` utilizado por la lógica de este bloque.
    let cancelled = false;
    setInviteVerifying(true);
    fetch(`${API_URL}/api/auth/${inviteKind === 'role' ? 'role-invite' : 'invite'}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: inviteToken }),
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success) {
          if (data.accountExists) {
            setInviteExistingAccount(true);
            setInviteSuccess(true);
            setInviteMessage(data.msg || 'Acceso habilitado. Inicia sesión con tu cuenta habitual.');
            setEmail(data.email || '');
            return;
          }
          // Los campos compartidos de registro son la única fuente de datos.
          // La invitación acredita exclusivamente el correo recibido, que no se puede
          // cambiar sin emitir una invitación nueva al destino correcto.
          setEmail(data.email || '');
          setInviteNotarioName(data.notarioName || '');
          setInviteRoleNames(data.roleNames?.length ? data.roleNames : ['Notario titular']);
          const fullName = String(data.notarioName || '').trim().split(/\s+/).filter(Boolean);
          setName(data.name || fullName[0] || '');
          setLastName(data.lastName || fullName.slice(1).join(' ') || '');
          // Notarios de México suelen registrar un teléfono local de 10 dígitos
          // (sin prefijo). No interpretarlo como un prefijo extranjero, p. ej. +81.
          const knownPhone = String(data.phone || '').trim();
          const knownDigits = knownPhone.replace(/\D/g, '');
          setPhone(knownDigits.length === 10 && !knownPhone.startsWith('+')
            ? formatPhoneNumber(`+52${knownDigits}`)
            : knownPhone ? formatPhoneNumber(knownPhone) : '+');
          setInviteVerified(true);
        } else {
          setInviteMessage(data.msg || 'La invitación no es válida.');
          setInviteVerified(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInviteMessage('No se pudo validar la invitación. Intenta de nuevo.');
          setInviteVerified(false);
        }
      })
      .finally(() => { if (!cancelled) setInviteVerifying(false); });
    return () => { cancelled = true; };
  }, [view, inviteToken, inviteKind, API_URL]);

  // Valor `phoneValidation` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const phoneValidation = useMemo(() => validatePhone(phone), [phone]);
  // Valor `emailValidation` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const emailValidation = useMemo(() => validateEmail(email), [email]);
  // Valor `nameValidation` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const nameValidation = useMemo(() => validateName(name), [name]);
  // Valor `lastNameValidation` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const lastNameValidation = useMemo(() => validateName(lastName), [lastName]);

  useEffect(() => { setEmailSuggestion(emailValidation.suggestion); }, [emailValidation.suggestion]);

  // La pantalla inicial replica el diseño; el teléfono se solicita al finalizar.
  const isRegisterDetailsComplete = useMemo(() => (
    nameValidation.valid && lastNameValidation.valid &&
    emailValidation.valid && password.trim() !== '' &&
    confirmPassword === password && acceptPolicies &&
    Object.values(passwordCriteria).every(Boolean)
  ), [nameValidation, lastNameValidation, emailValidation, password, confirmPassword, acceptPolicies, passwordCriteria]);

  // El backend requiere un teléfono de contacto válido.
  const isRegisterFormComplete = isRegisterDetailsComplete && phoneValidation.valid;

    // --- HANDLERS ---
  const handleNameChange = useCallback((setter) => (e) => {
    // Valor intermedio `filtered` utilizado por la lógica de este bloque.
    const filtered = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜàèìòùÀÈÌÒÙâêîôûÂÊÎÔÛäëïöüÄËÏÖÜçÇ\s'-]/g, '');
    setter(filtered);
  }, []);

  // Callback `handlePhoneChange` memoizado para conservar su referencia entre renderizados.
  const handlePhoneChange = useCallback((e) => {
    // Valor intermedio `inputValue` utilizado por la lógica de este bloque.
    const inputValue = e.target.value;
    // Valor intermedio `inputType` utilizado por la lógica de este bloque.
    const inputType = e.nativeEvent?.inputType;

    setPhone((previousValue) => {
      // Valor intermedio `digits` utilizado por la lógica de este bloque.
      let digits = inputValue.replace(/\D/g, '').slice(0, 15);
      // Valor intermedio `previousDigits` utilizado por la lógica de este bloque.
      const previousDigits = previousValue.replace(/\D/g, '');

      // Si Backspace eliminó solamente un separador generado por la máscara,
      // eliminar también el dígito anterior para que el campo no parezca trabado.
      if (
        inputType === 'deleteContentBackward' &&
        inputValue.length < previousValue.length &&
        digits === previousDigits
      ) {
        digits = digits.slice(0, -1);
      }

      // Valor intermedio `country` utilizado por la lógica de este bloque.
      const country = detectCountry(digits);
      if (country) {
        // Valor intermedio `maxN` utilizado por la lógica de este bloque.
        const maxN = Math.max(...country.digits);
        digits = digits.slice(0, country.code.length + maxN);
      }
      return formatPhoneNumber(digits);
    });
  }, []);

  // Callback `handleEmailInputChange` memoizado para conservar su referencia entre renderizados.
  const handleEmailInputChange = useCallback((setter, suggSetter) => (e) => {
    setter(e.target.value.replace(/\s/g, ''));
    if (suggSetter) suggSetter(null);
  }, []);

  // Callback `handleBlur` memoizado para conservar su referencia entre renderizados.
  const handleBlur = useCallback((field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  // Sugerencia de corrección para correo electrónico del registro.
  const applySuggestion = useCallback(() => {
    if (emailSuggestion) { setEmail(emailSuggestion); setEmailSuggestion(null); }
  }, [emailSuggestion]);

  // Función `preventCopyPaste` que encapsula una operación reutilizable de este módulo.
  const preventCopyPaste = (e) => { e.preventDefault(); };

  // Función `handleOtpChange` que gestiona la interacción o el evento correspondiente.
  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return;
    // Colección `newOtp` que reúne los elementos utilizados en este flujo.
    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);
    if (element.value !== "" && index < 5) otpInputs.current[index + 1].focus();
  };

  // Función `handleOtpKeyDown` que gestiona la interacción o el evento correspondiente.
  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) otpInputs.current[index - 1].focus();
  };

  // Función `handleNavigateToPrivacy` que gestiona la interacción o el evento correspondiente.
  const handleNavigateToPrivacy = (e) => { e.preventDefault(); if (onNavigate) onNavigate('legal-privacy'); };
  // Función `handleNavigateToTerms` que gestiona la interacción o el evento correspondiente.
  const handleNavigateToTerms = (e) => { e.preventDefault(); if (onNavigate) onNavigate('legal-terms'); };

  // --- SUBMITS ---

  // 🔴 FIX: Registro protegido contra múltiples envíos
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!isRegisterFormComplete || (isInvitation && (!inviteVerified || !inviteToken || inviteSuccess))) return;
    // 🔴 FIX: Prevenir doble envío
    if (isRegisterSubmitting) return;
    setIsRegisterSubmitting(true);

    setTouched({ name: true, lastName: true, email: true, confirmEmail: true, phone: true, password: true, confirmPassword: true });
    if (!emailValidation.valid || !phoneValidation.valid || !nameValidation.valid || !lastNameValidation.valid) {
      setRegisterError('Corrige los errores del formulario.');
      setIsRegisterSubmitting(false);
      return;
    }
    setRegisterError('');

    try {
      // Valor intermedio `endpoint` utilizado por la lógica de este bloque.
      const endpoint = isInvitation
        ? (inviteKind === 'role' ? '/api/auth/role-invite/accept' : '/api/auth/invite/accept')
        : '/api/auth/register';
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
          password,
          phone: normalizePhoneNumber(phone),
          ...(isInvitation ? { token: inviteToken, acceptedPolicies: acceptPolicies } : {}),
        }),
      });

      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json().catch(() => ({}));

      // Corrección: avanzar a la pantalla OTP solo si el servidor aceptó el registro.
      // Antes se cambiaba a `verify` antes de completar la petición y no se revisaba `res.ok`,
      // lo que dejaba al usuario atrapado si el servidor rechazaba el correo duplicado u otro dato.
      if (!res.ok) {
        logAuthApiError(endpoint, res, data);
        setRegisterError(data.msg || 'No se pudo completar el registro. Intenta de nuevo.');
        return;
      }

      if (isInvitation) {
        // El token verifica el correo y determina qué rol/notaría se vincula.
        // No se crea una cuenta adicional ni se envía un OTP de registro.
        setInviteExistingAccount(Boolean(data.accountExists));
        setInviteSuccess(true);
        setInviteMessage(data.msg || (data.accountExists ? 'Acceso habilitado.' : 'Cuenta creada. Ya puedes iniciar sesión.'));
        setRegisterPhoneStep(false);
        return;
      }
      setActiveUserEmail(email);
      setView('verify');
      setResendTimer(60);
    } catch (err) {
      console.error("Error register:", err);
      setRegisterError('Error de conexión. Verifica tu red e intenta de nuevo.');
    } finally {
      // 🔴 FIX: Re-habilitar después de 2 segundos
      setTimeout(() => setIsRegisterSubmitting(false), 2000);
    }
  };

  // Función `handleVerifySubmit` que gestiona la interacción o el evento correspondiente.
  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    // Valor intermedio `code` utilizado por la lógica de este bloque.
    const code = otp.join("");
    if (code.length < 6) return;
    setVerifyError("");
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: activeUserEmail, code }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg);
      setShowSuccessModal(true);
    } catch (err) { setVerifyError(err.message); }
  };

  // Función `handleResendCode` que gestiona la interacción o el evento correspondiente.
  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    setResendTimer(60);
    try {
      await fetch(`${API_URL}/api/auth/resend-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: activeUserEmail }),
      });
    } catch (e) { console.error(e); }
  };

  // 🔴 FIX: Login protegido contra múltiples envíos simultáneos
  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    // 🔴 FIX: Si ya estamos enviando, ignorar clicks adicionales
    if (isLoginSubmitting) return;
    setIsLoginSubmitting(true);
    setLoginMessage("");

    try {
      // Valor intermedio `endpoint` utilizado por la lógica de este bloque.
      const endpoint = '/api/auth/login';
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginEmail.trim().toLowerCase(),
          password: loginPassword,
          rememberMe,
        }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        logAuthApiError(endpoint, res, data);
        throw new Error(data.msg || 'No se pudo iniciar sesión. Intenta de nuevo.');
      }

      // 🔴 FIX: Login exitoso — llamar onLogin y NO re-habilitar el botón
      // (el usuario será redirigido, no necesita volver a hacer click)
      onLogin(data.userName, data.token, data.userEmail, data.userRole, rememberMe);
    } catch (err) {
      setLoginMessage(err.message);
      // Corrección: rehabilitar el control solo cuando hubo un error, para permitir otro intento.
      // Esperar 1.5 segundos para evitar spam inmediato
      setTimeout(() => setIsLoginSubmitting(false), 1500);
    }
  };

  // Forgot Password
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotMessage('');
    setForgotSuccess(false);
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg);
      setForgotSuccess(true);
      setForgotMessage(data.msg || 'Te enviamos un enlace de recuperación a tu correo.');
    } catch (err) {
      setForgotSuccess(false);
      setForgotMessage(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  // Reset Password
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!isResetFormComplete) return;
    setResetLoading(true);
    setResetMessage('');
    setResetSuccess(false);
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword: resetNewPassword }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg);
      setResetSuccess(true);
      setResetMessage(data.msg || '¡Contraseña actualizada!');
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      setResetMessage(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  // Función `goToLoginFromInvite` que controla la navegación o visibilidad asociada.
  const goToLoginFromInvite = () => {
    setName(''); setLastName(''); setEmail(''); setPhone('+');
    setPassword(''); setConfirmPassword(''); setAcceptPolicies(false);
    setRegisterError(''); setRegisterPhoneStep(false);
    setInviteMessage(''); setInviteSuccess(false); setInviteVerified(false);
    setView('login');
  };

  // --- RENDER ---
  // Inicio de sesión y registro comparten exactamente el mismo SVG de fondo.
  const useCrystalBackground = view === 'login' || view === 'register' || isInvitation;
  return (
    <section className={`auth-section-wisby${useCrystalBackground ? ' auth-section-wisby--glass' : ''}${view === 'login' ? ' auth-section-wisby--login' : ''}${view === 'register' || isInvitation ? ' auth-section-wisby--register' : ''}`}>
      {!useCrystalBackground && (
        <div className="video-background-container-auth">
          <video autoPlay loop muted playsInline><source src={loginVideo} type="video/mp4" /></video>
          <div className="video-overlay-auth"></div>
        </div>
      )}

      <div className="auth-card">

        {/* ===== LOGIN ===== */}
        {view === 'login' && (
          <form onSubmit={handleLoginSubmit}>
            <h2>INICIAR SESIÓN</h2>
            <div className="input-group">
              <input type="email" required value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value.replace(/\s/g, ''))}
                onPaste={preventCopyPaste} onCopy={preventCopyPaste} onCut={preventCopyPaste}
                disabled={isLoginSubmitting} />
              <label>CORREO ELECTRÓNICO</label>
            </div>
            <div className="input-group">
              <input type={showLoginPassword ? 'text' : 'password'} required value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onPaste={preventCopyPaste} onCopy={preventCopyPaste} onCut={preventCopyPaste}
                disabled={isLoginSubmitting} />
              <label>CONTRASEÑA</label>
              <span className="password-toggle-wisby" onClick={() => setShowLoginPassword(!showLoginPassword)}>
                {showLoginPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
              </span>
            </div>
            <div className="remember-me-container">
              <input type="checkbox" id="rm" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              <label htmlFor="rm">Recordar sesión</label>
            </div>
            {loginMessage && <p className="wisby-error-message">{loginMessage}</p>}

            <p className="auth-prompt forgot-link">
              <span onClick={() => { setView('forgotPassword'); setForgotEmail(''); setForgotMessage(''); setForgotSuccess(false); }}>
                ¿Olvidaste tu contraseña?
              </span>
            </p>

            <p className="auth-prompt">¿No tienes cuenta? <span onClick={() => setView('register')}>CREA UNA AQUÍ</span></p>

            {/* 🔴 FIX: Botón deshabilitado mientras envía */}
            <button
              type="submit"
              className={`auth-submit-button-wisby ${isLoginSubmitting ? 'disabled' : ''}`}
              disabled={isLoginSubmitting}
            >
              {isLoginSubmitting ? 'ENTRANDO...' : 'ENTRAR'}
            </button>
          </form>
        )}

        {/* ===== FORGOT PASSWORD ===== */}
        {view === 'forgotPassword' && (
          <div className="verify-container">
            <div className="wisby-verify-badge"><KeyIcon /></div>
            <h2>Recuperar Contraseña</h2>
            {!forgotSuccess ? (
              <>
                <p className="verify-desc">
                  Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
                </p>
                <form onSubmit={handleForgotPasswordSubmit}>
                  <div className="input-group">
                    <input type="email" required value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value.replace(/\s/g, ''))}
                      onPaste={preventCopyPaste} onCopy={preventCopyPaste} onCut={preventCopyPaste} />
                    <label>CORREO ELECTRÓNICO</label>
                  </div>
                  {forgotMessage && !forgotSuccess && <p className="wisby-error-message">{forgotMessage}</p>}
                  <button type="submit"
                    className={`auth-submit-button-wisby ${forgotLoading || !forgotEmail.trim() ? 'disabled' : ''}`}
                    disabled={forgotLoading || !forgotEmail.trim()}>
                    {forgotLoading ? 'ENVIANDO...' : 'ENVIAR ENLACE'}
                  </button>
                </form>
              </>
            ) : (
              <div className="forgot-success-state">
                <div className="success-icon-check" style={{ fontSize: '40px', marginBottom: '12px' }}>✉️</div>
                <p className="verify-desc" style={{ color: '#22c55e', fontWeight: 600 }}>{forgotMessage}</p>
                <p className="verify-desc" style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                  Revisa tu bandeja de entrada y carpeta de spam. El enlace expira en <strong>1 hora</strong>.
                </p>
              </div>
            )}
            <p className="auth-prompt"><span onClick={() => setView('login')}>Volver a Iniciar Sesión</span></p>
          </div>
        )}

        {/* ===== RESET PASSWORD ===== */}
        {view === 'resetPassword' && (
          <div className="verify-container">
            <div className="wisby-verify-badge"><KeyIcon /></div>
            <h2>{resetSuccess ? '¡Listo!' : 'Nueva Contraseña'}</h2>

            {!resetSuccess ? (
              <>
                <p className="verify-desc">
                  Ingresa tu nueva contraseña
                </p>
                <form onSubmit={handleResetPasswordSubmit}>
                  <div className="input-group has-feedback">
                    <input type={showResetPassword ? 'text' : 'password'} required value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e.target.value)}
                      onPaste={preventCopyPaste} onCopy={preventCopyPaste} onCut={preventCopyPaste} />
                    <label>NUEVA CONTRASEÑA</label>
                    <span className="password-toggle-wisby" onClick={() => setShowResetPassword(!showResetPassword)}>
                      {showResetPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                    </span>
                  </div>

                  <div className="password-requirements-compact">
                    <div className={`req-item-compact ${resetNewPassword.length > 0 && resetPasswordCriteria.length ? 'valid' : ''} ${resetNewPassword.length > 0 ? 'visible' : ''}`}>
                      <CheckIcon active={resetNewPassword.length > 0 && resetPasswordCriteria.length} /> Mín. 8 caracteres
                    </div>
                    <div className={`req-item-compact ${resetNewPassword.length > 0 && resetPasswordCriteria.number ? 'valid' : ''} ${resetNewPassword.length > 0 ? 'visible' : ''}`}>
                      <CheckIcon active={resetNewPassword.length > 0 && resetPasswordCriteria.number} /> Mín. 1 número
                    </div>
                    <div className={`req-item-compact ${resetNewPassword.length > 0 && resetPasswordCriteria.case ? 'valid' : ''} ${resetNewPassword.length > 0 ? 'visible' : ''}`}>
                      <CheckIcon active={resetNewPassword.length > 0 && resetPasswordCriteria.case} /> Mayúscula y minúscula
                    </div>
                    <div className={`req-item-compact ${resetNewPassword.length > 0 && resetPasswordCriteria.special ? 'valid' : ''} ${resetNewPassword.length > 0 ? 'visible' : ''}`}>
                      <CheckIcon active={resetNewPassword.length > 0 && resetPasswordCriteria.special} /> Carácter especial
                    </div>
                  </div>

                  <div className={`input-group has-feedback ${resetConfirmPassword ? (resetConfirmPassword === resetNewPassword && Object.values(resetPasswordCriteria).every(Boolean) ? 'field-valid' : 'field-invalid') : ''}`}>
                    <input type={showResetConfirmPassword ? 'text' : 'password'} required value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      onPaste={preventCopyPaste} onCopy={preventCopyPaste} onCut={preventCopyPaste} />
                    <label>CONFIRMAR CONTRASEÑA</label>
                    <span className="password-toggle-wisby" onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}>
                      {showResetConfirmPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                    </span>
                    <div className="feedback-slot">
                      {resetConfirmPassword && resetConfirmPassword !== resetNewPassword && <span className="field-error-msg">No coinciden</span>}
                    </div>
                  </div>

                  {resetMessage && !resetSuccess && <p className="wisby-error-message">{resetMessage}</p>}

                  <button type="submit"
                    className={`auth-submit-button-wisby ${!isResetFormComplete || resetLoading ? 'disabled' : ''}`}
                    disabled={!isResetFormComplete || resetLoading}>
                    {resetLoading ? 'ACTUALIZANDO...' : 'ACTUALIZAR CONTRASEÑA'}
                  </button>
                </form>
                <p className="auth-prompt"><span onClick={() => setView('login')}>Volver a Iniciar Sesión</span></p>
              </>
            ) : (
              <>
                <div className="success-icon-check" style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                <p className="verify-desc" style={{ color: '#22c55e', fontWeight: 600 }}>{resetMessage}</p>
                <button className="auth-submit-button-wisby"
                  onClick={() => { setView('login'); setResetNewPassword(''); setResetConfirmPassword(''); setResetMessage(''); setResetSuccess(false); }}>
                  IR A INICIAR SESIÓN
                </button>
              </>
            )}
          </div>
        )}

        {/* Todas las invitaciones utilizan el mismo registro Crystal. */}
        {isInvitation && (inviteVerifying || !inviteVerified || inviteSuccess) && (
          <div className="register-form-wisby register-invite-status" role="status">
            <h2>{inviteSuccess ? (inviteExistingAccount ? '¡ACCESO HABILITADO!' : '¡CUENTA CREADA!') : inviteVerifying ? 'VALIDANDO INVITACIÓN' : 'INVITACIÓN NO DISPONIBLE'}</h2>
            <p>{inviteSuccess
              ? inviteMessage
              : inviteVerifying
                ? 'Estamos comprobando tu invitación para continuar con el registro.'
                : (inviteMessage || 'Este enlace ya se utilizó, fue revocado o ha expirado. Solicita una invitación nueva al administrador.')}</p>
            {!inviteVerifying && (
              <button type="button" className="auth-submit-button-wisby register-main-action"
                onClick={goToLoginFromInvite}>IR A INICIAR SESIÓN</button>
            )}
          </div>
        )}

        {/* ===== REGISTRO CRYSTAL: registro normal o invitación validada ===== */}
        {(view === 'register' || (isInvitation && inviteVerified && !inviteVerifying && !inviteSuccess)) && (
          <form className="register-form-wisby" onSubmit={registerPhoneStep ? handleRegisterSubmit : (e) => {
            e.preventDefault();
            if (isRegisterDetailsComplete && !isRegisterSubmitting) {
              setRegisterError('');
              setRegisterPhoneStep(true);
            }
          }}>
            {registerPhoneStep ? (
              <div className="register-phone-step">
                <h2>UN ÚLTIMO PASO</h2>
                <p>Para terminar de crear tu cuenta, necesitamos un teléfono de contacto.</p>
                <div className="register-field register-field--full">
                  <label htmlFor="reg-phone">TELÉFONO *</label>
                  <input id="reg-phone" type="tel" value={phone} onChange={handlePhoneChange}
                    onBlur={handleBlur('phone')} required inputMode="tel" autoComplete="tel"
                    placeholder="Ej. (+52) 81-1234-5678" disabled={isRegisterSubmitting}
                    aria-invalid={Boolean(touched.phone && phoneValidation.error)} />
                  {touched.phone && phoneValidation.error && <small className="register-field-error" role="alert">{phoneValidation.error}</small>}
                </div>
                {registerError && <p className="wisby-error-message" role="alert">{registerError}</p>}
                <button type="submit" className="auth-submit-button-wisby register-main-action"
                  disabled={!isRegisterFormComplete || isRegisterSubmitting}>
                  {isRegisterSubmitting ? 'CREANDO CUENTA...' : 'CONFIRMAR Y CREAR CUENTA'}
                </button>
                <button type="button" className="register-back-link"
                  disabled={isRegisterSubmitting}
                  onClick={() => { setRegisterError(''); setRegisterPhoneStep(false); }}>Volver a mis datos</button>
              </div>
            ) : (
              <>
                <h2>CREAR CUENTA</h2>
                {isInvitation && (
                  <p className="register-invitation-context" id="role-invitation-context">
                    Has sido invitado a Wisby como <strong>{inviteRoleNames.join(', ')}</strong>
                    {inviteNotarioName ? <> · {inviteNotarioName}</> : null}.
                    Revisa tus datos precargados y corrige lo necesario para activar tu acceso.
                  </p>
                )}
                {!isInvitation && <p className="register-mobile-login">¿Ya tienes cuenta?{' '}
                  <button type="button" className="register-mobile-login-link" onClick={() => setView('login')}>Inicia sesión</button>
                </p>}
                <div className="register-layout-grid">
                  <div className="register-field">
                    <label htmlFor="reg-name">NOMBRE(S) *</label>
                    <input id="reg-name" type="text" value={name} onChange={handleNameChange(setName)}
                      onBlur={handleBlur('name')} required maxLength={50} autoComplete="given-name"
                      placeholder="Ej. Alejandro" disabled={isRegisterSubmitting} />
                    {touched.name && nameValidation.error && <small className="register-field-error" role="alert">{nameValidation.error}</small>}
                  </div>
                  <div className="register-field">
                    <label htmlFor="reg-last-name">APELLIDO(S) *</label>
                    <input id="reg-last-name" type="text" value={lastName} onChange={handleNameChange(setLastName)}
                      onBlur={handleBlur('lastName')} required maxLength={50} autoComplete="family-name"
                      placeholder="Ej. Acosta Murillo" disabled={isRegisterSubmitting} />
                    {touched.lastName && lastNameValidation.error && <small className="register-field-error" role="alert">{lastNameValidation.error}</small>}
                  </div>
                  <div className="register-field register-field--full">
                    <label htmlFor="reg-email">CORREO ELECTRÓNICO *</label>
                    <input id="reg-email" type="email" value={email}
                      onChange={isInvitation ? undefined : handleEmailInputChange(setEmail, setEmailSuggestion)}
                      readOnly={isInvitation}
                      onBlur={handleBlur('email')} required maxLength={254} autoComplete="email"
                      placeholder="tu@correo.com" disabled={isRegisterSubmitting} />
                    {isInvitation && <small className="register-invitation-email-note">Correo confirmado por invitación. Para cambiarlo, solicita un enlace nuevo al correo correcto.</small>}
                    {touched.email && emailValidation.error && <small className="register-field-error" role="alert">{emailValidation.error}</small>}
                    {touched.email && emailSuggestion && <small className="register-field-error">
                      ¿Quisiste decir <button type="button" onClick={() => applySuggestion('email')}>{emailSuggestion}</button>?
                    </small>}
                  </div>
                  <div className="register-field register-field--password">
                    <label htmlFor="reg-password">CONTRASEÑA *</label>
                    <div className="register-password-input">
                      <input id="reg-password" type={showPassword ? 'text' : 'password'}
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        onBlur={handleBlur('password')} required minLength={8} maxLength={128}
                        autoComplete="new-password" placeholder="Crea una contraseña" disabled={isRegisterSubmitting} />
                      <button type="button" aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="register-eye-toggle" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                      </button>
                    </div>
                  </div>
                  <div className="register-field register-field--password">
                    <label htmlFor="reg-confirm-password">CONFIRMA LA CONTRASEÑA *</label>
                    <div className="register-password-input">
                      <input id="reg-confirm-password" type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                        onBlur={handleBlur('confirmPassword')} required minLength={8} maxLength={128}
                        autoComplete="new-password" placeholder="Confirma tu contraseña" disabled={isRegisterSubmitting} />
                      <button type="button" aria-label={showConfirmPassword ? 'Ocultar confirmación' : 'Mostrar confirmación'}
                        className="register-eye-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                      </button>
                    </div>
                    <span className={'register-password-match' + (confirmPassword && confirmPassword !== password ? ' is-error' : '')} role="status">
                      {confirmPassword && (confirmPassword === password ? '✓ Las contraseñas coinciden' : '× Las contraseñas no coinciden')}
                    </span>
                  </div>
                </div>
                <div className="register-password-checks" aria-label="Requisitos de contraseña">
                  {[
                    ['length', 'Mín. 8 caracteres'],
                    ['case', 'Mayúscula y minúscula'],
                    ['number', 'Mín. 1 número'],
                    ['special', 'Carácter especial']
                  ].map(([key, label]) => (
                    <div key={key} className={'register-criterion' + (passwordCriteria[key] ? ' is-valid' : '')}>
                      <span className="register-criterion-dot" aria-hidden="true">{passwordCriteria[key] ? '✓' : ''}</span>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="register-terms">
                  <input id="reg-policies" type="checkbox" checked={acceptPolicies}
                    onChange={(e) => setAcceptPolicies(e.target.checked)} disabled={isRegisterSubmitting} />
                  <label htmlFor="reg-policies">Acepto el{' '}
                    <button type="button" className="auth-link-inline" onClick={handleNavigateToPrivacy}>Aviso de Privacidad</button>
                    {' '}y{' '}
                    <button type="button" className="auth-link-inline" onClick={handleNavigateToTerms}>Términos de Uso</button>{' '}*
                  </label>
                </div>
                {registerError && <p className="wisby-error-message" role="alert">{registerError}</p>}
                <button type="submit" className="auth-submit-button-wisby register-main-action"
                  disabled={!isRegisterDetailsComplete || isRegisterSubmitting}>CREAR CUENTA</button>
                <div className="register-social-divider"><span>O crea tu cuenta con</span></div>
                <div className="register-social-providers" aria-label="Proveedores disponibles próximamente">
                  <button type="button" disabled title="Próximamente: Google" aria-label="Google (próximamente)">
                    <span className="register-google-mark" aria-hidden="true">G</span>Google
                  </button>
                  <button type="button" disabled title="Próximamente: Microsoft" aria-label="Microsoft (próximamente)">
                    <span className="register-microsoft-mark" aria-hidden="true"><i/><i/><i/><i/></span>Microsoft
                  </button>
                  <button type="button" disabled title="Próximamente: Apple" aria-label="Apple (próximamente)">
                    <svg viewBox="0 0 24 24" width="23" height="23" fill="currentColor" aria-hidden="true">
                      <path d="M17.1 12.54c.02 2.55 2.24 3.41 2.26 3.42-.02.05-.35 1.2-1.17 2.39-.71 1.02-1.44 2.04-2.6 2.07-1.14.02-1.5-.67-2.81-.67-1.3 0-1.71.65-2.79.69-1.13.04-1.99-1.13-2.71-2.14-1.48-2.07-2.61-5.86-1.09-8.42a4.2 4.2 0 0 1 3.5-2.14c1.09-.02 2.12.74 2.8.74.67 0 1.94-.92 3.27-.78.55.02 2.09.22 3.08 1.67-.08.04-1.84 1.07-1.82 3.17ZM15.45 6.27A4.09 4.09 0 0 0 16.39 3a4.17 4.17 0 0 0-2.7 1.55 3.9 3.9 0 0 0-.97 3.12 3.5 3.5 0 0 0 2.73-1.4Z"/>
                    </svg>Apple
                  </button>
                </div>
                <p className="register-social-note">Registro con Google, Microsoft y Apple próximamente.</p>
              </>
            )}
          </form>
        )}

        {/* ===== VERIFICACIÓN ===== */}
        {view === 'verify' && (
          <div className="verify-container">
            <div className="wisby-verify-badge"><MailVerifyIcon /></div>
            <h2>Verifica tu correo</h2>
            <p className="verify-desc">
              Hemos enviado un código de 6 dígitos a <strong>{activeUserEmail}</strong>. Ingrésalo a continuación.
            </p>
            <div className="otp-input-wrapper">
              {otp.map((data, index) => (
                <input key={index} type="text" maxLength="1" className="otp-field" value={data}
                  ref={el => otpInputs.current[index] = el}
                  onChange={e => handleOtpChange(e.target, index)}
                  onKeyDown={e => handleOtpKeyDown(e, index)}
                  onPaste={preventCopyPaste} onCopy={preventCopyPaste} onCut={preventCopyPaste} />
              ))}
            </div>
            {verifyError && <p className="wisby-error-message">{verifyError}</p>}
            <button className="auth-submit-button-wisby" onClick={handleVerifySubmit}
              disabled={otp.join("").length < 6}>VERIFICAR</button>
            <div className="resend-section">
              <p className="resend-text">¿No recibiste el código?</p>
              <div className="resend-controls">
                <button className={`resend-link-btn ${resendTimer > 0 ? 'disabled' : ''}`}
                  onClick={handleResendCode} disabled={resendTimer > 0}>Reenviar código</button>
                {resendTimer > 0 && <CircularTimer seconds={resendTimer} />}
              </div>
            </div>
            <p className="auth-prompt"><span onClick={() => setView('register')}>Volver al Registro</span></p>
          </div>
        )}
      </div>

      {/* ===== MODAL ÉXITO ===== */}
      {showSuccessModal && (
        <div className="wisby-success-overlay">
          <div className="wisby-success-card">
            <div className="success-icon-check">✓</div>
            <h3>¡Verificación Exitosa!</h3>
            <p>Tu cuenta ha sido activada correctamente. Ya puedes acceder a nuestros servicios.</p>
            <button className="success-btn-wisby"
              onClick={() => { setShowSuccessModal(false); setView('login'); }}>
              Ir a Iniciar Sesión
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
